import React from "react";
import { FiChevronLeft } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

function LeftSide() {
  const navigate = useNavigate();

  const {
    pickup,
    dropoff,
    distance,
    duration,
    time,
    date,
    passenger,
    luggage,
    booster,
    regular,
    infant,
    fleet_selection,
  } = useSelector((state) => state.booking);

  const formatDisplayDate = (d) => {
    if (!d) return "Today";
    // Check if format is already MM/DD/YYYY or DD/MM/YYYY or YYYY-MM-DD
    const str = String(d).trim();
    if (str.includes('-')) {
      const parts = str.split('-');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY-MM-DD -> MM/DD/YYYY
          return `${parts[1]}/${parts[2]}/${parts[0]}`;
        } else if (parts[2].length === 4) {
          // DD-MM-YYYY -> MM/DD/YYYY
          return `${parts[1]}/${parts[0]}/${parts[2]}`;
        }
      }
    }
    if (str.includes('/')) {
      const parts = str.split('/');
      if (parts.length === 3) {
        // If first part is > 12, it is definitely DD/MM/YYYY
        if (parseInt(parts[0], 10) > 12) {
          return `${parts[1]}/${parts[0]}/${parts[2]}`;
        }
        return str;
      }
    }
    return str;
  };

  const getFormattedFare = () => {
    const vehicleFare = fleet_selection?.fare_details?.vehicle_fare || 0;
    const totalFare = fleet_selection?.fare_details?.total_fare || 0;
    const finalFare = fleet_selection?.fare_details?.final_fare || 0;
    const journeyFare = fleet_selection?.fare_details?.journey_fare || 0;
    
    const fare = finalFare || totalFare || journeyFare || vehicleFare;
    if (fare === 0 && fleet_selection?.passengers) {
      const placeholderPrices = { 2: 45.00, 3: 55.00, 4: 65.00, 5: 65.00, 6: 85.00, 7: 85.00 };
      return (placeholderPrices[fleet_selection.passengers] || 75.00).toFixed(2);
    }
    return Number(fare).toFixed(2);
  };

  return (
    <div className="w-full h-full flex flex-col justify-between p-2">
      <div>
        <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-800">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors py-1.5 px-3 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700"
          >
            <FiChevronLeft size={16} />
            <span>Modify Search</span>
          </button>

          {fleet_selection?.name && (
            <div className="text-right">
              <span className="text-[10px] text-slate-400 block uppercase font-bold">Estimated Fare</span>
              <span className="text-xl font-black text-white font-mono">
                ${getFormattedFare()}
              </span>
            </div>
          )}
        </div>

        {/* Selected Vehicle Snippet if picked */}
        {fleet_selection?.name && (
          <div className="p-3.5 mb-5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center gap-3">
            <div className="w-14 h-10 rounded-lg bg-slate-900 p-1 flex items-center justify-center border border-slate-800">
              <img 
                src={fleet_selection?.info?.img_link || 'https://dispatch.bostonairportexpresscar.com/assets/images/fleet/1.png'} 
                alt="Selected Vehicle" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-blue-400">Class Selected</span>
              <h4 className="text-xs font-bold text-white">{fleet_selection.name}</h4>
            </div>
          </div>
        )}

        {/* Corporate Itinerary Card */}
        <div className="space-y-4 text-xs">
          <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 space-y-4">
            
            {/* Route Points */}
            <div className="space-y-3 relative pl-4 border-l-2 border-slate-700">
              {/* Pickup */}
              <div className="relative">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 absolute -left-[21px] top-1 ring-4 ring-slate-900" />
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Pickup Location</span>
                <p className="text-white font-medium text-xs mt-0.5 line-clamp-2">{pickup?.address || "Pickup address not set"}</p>
              </div>

              {/* Dropoff */}
              <div className="relative pt-2">
                <span className="w-2.5 h-2.5 rounded-full bg-sky-400 absolute -left-[21px] top-3 ring-4 ring-slate-900" />
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Dropoff Location</span>
                <p className="text-white font-medium text-xs mt-0.5 line-clamp-2">{dropoff?.address || "Destination address not set"}</p>
              </div>
            </div>

            {/* Travel Specs */}
            <div className="pt-3 border-t border-slate-800 grid grid-cols-2 gap-3 text-[11px]">
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold">Trip Distance</span>
                <span className="text-white font-bold font-mono mt-0.5 block">{distance || "--"}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold">Est. Duration</span>
                <span className="text-white font-bold font-mono mt-0.5 block">{duration || "--"}</span>
              </div>
            </div>

          </div>

          {/* Date & Time Details */}
          <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5 text-xs">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Pickup Schedule</span>
                <span className="text-white font-medium">{formatDisplayDate(date)} • {time || "Immediate"}</span>
              </div>
              <div className="text-right">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Occupancy</span>
                <span className="text-white font-medium">{passenger || 1} Pax • {luggage || 0} Bags</span>
              </div>
            </div>

            {/* Child Seats Breakdown if selected */}
            {(Number(booster) > 0 || Number(regular) > 0 || Number(infant) > 0) && (
              <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[11px]">
                <span className="text-slate-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-emerald-400 !text-sm">child_care</span>
                  Child Seats:
                </span>
                <div className="flex items-center gap-2 font-medium text-emerald-300">
                  {Number(booster) > 0 && <span>{booster} Booster</span>}
                  {Number(regular) > 0 && <span>{regular} Regular</span>}
                  {Number(infant) > 0 && <span>{infant} Infant</span>}
                </div>
              </div>
            )}
          </div>

          {/* Inclusions */}
          <div className="p-3.5 rounded-xl bg-slate-900/60 border border-slate-800/80 space-y-2 text-[11px] text-slate-300">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-400 !text-sm">verified</span>
              <span>All tolls, Logan Airport fees included</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-400 !text-sm">verified</span>
              <span>Guaranteed on-time driver arrival</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-slate-800 text-center">
        <p className="text-[11px] text-slate-400">Need immediate assistance or phone booking?</p>
        <a href="tel:6174997770" className="text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors mt-0.5 inline-block font-mono">
          Call (617) 499-7770
        </a>
      </div>
    </div>
  );
}

export default LeftSide;
